import React, { useRef } from 'react';
import { Divider, Paper } from '@mui/material';

import { FeaturePatternTrussContainer } from '@paragon/contract-library';
import {
  BeamSchema,
  CeilingPlaneSchema,
  CeilingTraySchema,
  DataRow,
  FeatureAreaSchema,
  FeaturePatternContainerSchema,
  getChildrenOfDataRow,
  ModelChildLoader,
  ProjectSchema,
  RoofContainerSchema,
  RoofPlaneSchema,
  SnappingUnderlaySchema,
  TrussEnvelopeSchema,
  TrussGroupSchema,
  WallSchema,
  WorkLineSchema,
} from '@paragon/layout-models';

import { getProjectLayoutModel } from '../../../../store/selectors/getProjectLayoutModel';
import {
  getAllSnappingUnderlays,
  getVisibleBearingEnvelopes,
  getVisibleCeilingPlanes,
  getVisibleCeilingTrays,
  getVisibleFeaturePatternContainers,
  getVisibleRoofContainers,
  getVisibleRoofPlanes,
  getVisibleTrussEnvelopes,
  getVisibleWorkLines,
} from '../../../../store/selectors/layoutDataSelectors';
import {
  isLayoutBasedOnRoofContainers,
  isLayoutBasedOnRoofPlanes,
} from '../../../../store/selectors/layoutModeSelectors';
import { useAppSelector } from '../../../hooks';
import { DesignClientGrid, GridMethods } from './DesignClientGrid';
import ButtonBar from './header-buttons';
import { GridAutoFocusPlugin } from './row-management/GridAutoFocusPlugin';

const CONTAINER_LIKE_TYPES = ['project', 'roofContainer'];

function getContainerFeatureArea(container: FeaturePatternTrussContainer) {
  return container.featureAreas;
}

interface Props {
  gridHeight: number;
}

export default function LayoutPanel({ gridHeight }: Props) {
  const project = useAppSelector(getProjectLayoutModel);

  const trussEnvelopes = useAppSelector(getVisibleTrussEnvelopes);
  const trussGroups = useAppSelector((state) => state.trussGroups);
  const roofContainers = useAppSelector(getVisibleRoofContainers);
  const bearingEnvelopes = useAppSelector(getVisibleBearingEnvelopes);
  const featurePatternContainers = useAppSelector(getVisibleFeaturePatternContainers);
  const workLines = useAppSelector(getVisibleWorkLines);
  const roofPlanes = useAppSelector(getVisibleRoofPlanes);
  const ceilingPlanes = useAppSelector(getVisibleCeilingPlanes);
  const ceilingTrays = useAppSelector(getVisibleCeilingTrays);
  const snappingUnderlays = useAppSelector(getAllSnappingUnderlays);

  const layoutBasedOnRoofContainers = useAppSelector(isLayoutBasedOnRoofContainers);
  const layoutBasedOnRoofPlanes = useAppSelector(isLayoutBasedOnRoofPlanes);

  const gridRef = useRef<GridMethods>(null);

  if (project == null) return null;

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // defining containers
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  let containers = [];

  containers.push('workLine');
  containers.push('snappingUnderlay');
  containers.push('project.trussGroup');

  if (layoutBasedOnRoofContainers) {
    containers.push('project.roofContainer');
  } else if (layoutBasedOnRoofPlanes) {
    containers = containers.concat([
      'project.beam',
      'project.wall',
      'project.featurePatternContainer',
      'project.roofPlane',
      'project.ceilingPlane',
      'project.ceilingTray',
      'project.trussEnvelope',
    ]);
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // defining loaders
  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  const loader = new ModelChildLoader();

  loader.add(ProjectSchema, WorkLineSchema, () => workLines);
  loader.add(ProjectSchema, SnappingUnderlaySchema, () => snappingUnderlays);
  loader.add(ProjectSchema, RoofContainerSchema, () => roofContainers);

  loader.add(ProjectSchema, FeaturePatternContainerSchema, () => featurePatternContainers);
  loader.add(ProjectSchema, BeamSchema, () =>
    bearingEnvelopes.filter((bearingEnvelope) => bearingEnvelope.bearingType === 'Beam'),
  );
  loader.add(ProjectSchema, WallSchema, () =>
    bearingEnvelopes.filter((bearingEnvelope) => bearingEnvelope.bearingType === 'Wall'),
  );
  loader.add(RoofContainerSchema, TrussEnvelopeSchema, () => trussEnvelopes);

  loader.add(ProjectSchema, RoofPlaneSchema, () => roofPlanes);
  loader.add(ProjectSchema, CeilingPlaneSchema, () => ceilingPlanes);
  loader.add(ProjectSchema, CeilingTraySchema, () => ceilingTrays);

  loader.add(ProjectSchema, TrussEnvelopeSchema, () =>
    layoutBasedOnRoofPlanes ? trussEnvelopes : [/* truss envelopes are found under roof containers instead */],
  );

  loader.add(ProjectSchema, TrussGroupSchema, () =>
    Object.values(trussGroups).filter((trussGroup) => trussGroup.parentGuid === null),
  );
  loader.add(TrussGroupSchema, TrussGroupSchema, (parent) =>
    Object.values(trussGroups).filter((trussGroup) => trussGroup.parentGuid === parent.guid),
  );

  loader.add(BeamSchema, RoofPlaneSchema, (parent) =>
    roofPlanes.filter(
      (plane) => plane.geometry.type === 'BearingEnvelope' && plane.geometry.bearingEnvelopeGuid === parent.guid,
    ),
  );
  loader.add(BeamSchema, CeilingPlaneSchema, (parent) =>
    ceilingPlanes.filter(
      (plane) => plane.geometry.type === 'BearingEnvelope' && plane.geometry.bearingEnvelopeGuid === parent.guid,
    ),
  );
  loader.add(WallSchema, RoofPlaneSchema, (parent) =>
    roofPlanes.filter(
      (plane) => plane.geometry.type === 'BearingEnvelope' && plane.geometry.bearingEnvelopeGuid === parent.guid,
    ),
  );
  loader.add(WallSchema, CeilingPlaneSchema, (parent) =>
    ceilingPlanes.filter(
      (plane) => plane.geometry.type === 'BearingEnvelope' && plane.geometry.bearingEnvelopeGuid === parent.guid,
    ),
  );

  loader.add(FeaturePatternContainerSchema, FeatureAreaSchema, getContainerFeatureArea);

  const projectRow = new DataRow(project, ProjectSchema);

  const topLevelRows = getChildrenOfDataRow(projectRow, { loaders: loader, containers });

  return (
    <div>
      <Paper variant="outlined" elevation={0}>
        <ButtonBar
          panelContext="layout"
          onFocusItemRequested={(item) => {
            gridRef.current?.focusRow(item);
          }}
        />
        <Divider />
        <DesignClientGrid
          ref={gridRef}
          topLevelRows={topLevelRows}
          loaders={loader}
          containers={containers}
          containerLikeTypes={CONTAINER_LIKE_TYPES}
          height={gridHeight}
        />
      </Paper>

      <GridAutoFocusPlugin gridRef={gridRef} />
    </div>
  );
}
